import { useMemo } from 'react';

import { X } from 'lucide-react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import { DEFAULT_COLOR_PALETTE } from '@/lib/constants/colors';
import { cn } from '@/lib/utils';

import { SimpleSingleSelect } from './simple-single-select';
import { SingleSelectCommandListComponent } from './single-select-command-list';
import { useFilteredOptions, useSelectState } from './single-select-hooks';
import { createDefaultOptionRenderer } from './single-select-option-renderers';
import { DefaultTriggerRenderer } from './single-select-trigger-renderer';
import { SingleSelectProps } from './single-select-types';
import { SingleSelectVirtualizedList } from './single-select-virtualized-list';

const VIRTUALIZATION_THRESHOLD = 35;

/**
 * A highly customizable single-select dropdown component with React Hook Form integration.
 * Automatically switches between simple Select and advanced Command-based implementations based on features needed.
 *
 * **Key Features:**
 * - **Dual Implementation**: Uses shadcn Select for simple cases, Command for advanced features
 * - **React Hook Form Integration**: Full validation and error handling support
 * - **Rich Content Support**: Avatars, descriptions, custom rendering, and search functionality
 * - **Granular Description Control**: Separate control for descriptions in options vs selector
 * - **Smart Performance**: Only loads complex UI when search or advanced features are needed
 * - **Accessibility**: Full keyboard navigation, screen reader support, and ARIA compliance
 * - **Customizable**: Extensive theming, rendering, and behavior customization options
 * - **Loading States**: Built-in loading handling with disabled states
 * - **Flexible Filtering**: Both client-side and server-side search support
 *
 * **Implementation Strategy:**
 * - **Simple Mode** (`searchable={false}`): Uses optimized shadcn Select component
 * - **Advanced Mode** (`searchable={true}`): Uses Command with Popover for rich features
 *
 * @example
 * ```tsx
 * // Basic usage
 * <SingleSelect
 *   name="managerId"
 *   control={form.control}
 *   label="Project Manager"
 *   placeholder="Select a manager"
 *   options={[
 *     { value: "1", label: "John Doe" },
 *     { value: "2", label: "Jane Smith" },
 *   ]}
 * />
 *
 * // With avatars and descriptions
 * <SingleSelect
 *   name="assignee"
 *   control={form.control}
 *   showAvatars={true}
 *   showDescriptionInOptions={true}
 *   showDescriptionInSelector={true}
 *   options={userOptions}
 * />
 *
 * // Server-side filtering
 * <SingleSelect
 *   name="project"
 *   control={form.control}
 *   searchable={true}
 *   enableClientSideFiltering={false}
 *   onSearchChange={handleSearch}
 *   options={serverFilteredOptions}
 * />
 * ```
 */
export function SingleSelect<T extends FieldValues>({
  name,
  control,
  options,
  label,
  placeholder,
  emptyMessage,
  noOptionsMessage,
  searchPlaceholder,
  disabled = false,
  loading = false,
  className,
  formItemClassName,
  showFormMessage = true,
  searchable = true,
  hideSearchWhenEmpty = false,
  enableClientSideFiltering = true,
  showAvatars = false,
  showDescriptionInOptions = false,
  showDescriptionInSelector = false,
  avatarShape = 'circle',
  avatarSize,
  maxHeight = '300px',
  renderOption,
  renderTrigger,
  renderGroupHeading,
  selectedOption: overrideSelectedOption,
  initialSelectedItem,
  isCreatable = false,
  onCreateOption,
  onOpenChange,
  onSearchChange,
  onChange,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
  disableVirtualization = false,
  warning,
  isClearSelectionEnabled = false,
}: SingleSelectProps<T>) {
  const { t } = useTranslation(['common']);
  const optionsKey = useMemo(() => options.length, [options.length]);
  const { isOpen, searchValue, handleOpenChange, handleSearchChange } = useSelectState({
    onOpenChange,
    onSearchChange,
  });

  const defaultPlaceholder = placeholder || t('selectOption');
  const defaultEmptyMessage = emptyMessage || t('noResultsFound');
  const defaultSearchPlaceholder = searchPlaceholder || t('searchPlaceholder');

  const filteredOptions = useFilteredOptions({
    options,
    searchValue,
    searchable,
    enableClientSideFiltering,
  });

  const shouldVirtualize = !disableVirtualization && options.length >= VIRTUALIZATION_THRESHOLD;
  const useSimpleSelect = !searchable && options.length < VIRTUALIZATION_THRESHOLD;

  const { setContainerElement, setSentinelElement } = useInfiniteScroll({
    hasNextPage,
    isFetching: isFetchingNextPage,
    onLoadMore: fetchNextPage || (() => {}),
  });

  const defaultRenderOption = useMemo(
    () =>
      createDefaultOptionRenderer({
        showAvatars,
        showDescriptionInOptions,
        avatarShape,
        avatarSize,
      }),
    [showAvatars, showDescriptionInOptions, avatarShape, avatarSize]
  );

  // Simple select for non-searchable small lists
  if (useSimpleSelect) {
    return (
      <SimpleSingleSelect
        name={name}
        control={control}
        options={options}
        label={label}
        placeholder={defaultPlaceholder}
        noOptionsMessage={noOptionsMessage}
        disabled={disabled}
        loading={loading}
        className={className}
        formItemClassName={formItemClassName}
        showFormMessage={showFormMessage}
        showAvatars={showAvatars}
        showDescriptionInOptions={showDescriptionInOptions}
        avatarShape={avatarShape}
        avatarSize={avatarSize}
        renderOption={renderOption}
        renderTrigger={renderTrigger}
        onChange={onChange}
        optionsKey={optionsKey}
        warning={warning}
        initialSelectedItem={initialSelectedItem}
        isClearSelectionEnabled={isClearSelectionEnabled}
      />
    );
  }

  // Complex Command-based interface for searchable or large lists
  return (
    <FormField
      control={control}
      warning={warning}
      name={name}
      render={({ field }) => {
        // Determine the selected option:
        // 1. If overrideSelectedOption is provided, use it
        // 2. If initialSelectedItem is provided and matches current value, use it
        // 3. Otherwise, find the option in the options array
        const selectedOption =
          overrideSelectedOption ||
          (initialSelectedItem && initialSelectedItem.value === field.value
            ? initialSelectedItem
            : null) ||
          options.find(option => option.value === field.value) ||
          null;

        const handleSelectOption = (value: string) => {
          field.onChange(value);
          if (onChange) {
            onChange(value);
          }
          handleOpenChange(false);
        };

        const showClearButton = isClearSelectionEnabled && selectedOption && !disabled && !loading;

        const handleClear = () => {
          field.onChange('');
          onChange?.('');
        };

        return (
          <FormItem className={cn('flex flex-col', formItemClassName)}>
            {label && <FormLabel>{label}</FormLabel>}
            <div className="relative">
              <Popover
                open={disabled ? false : isOpen}
                onOpenChange={disabled ? undefined : handleOpenChange}
              >
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={isOpen}
                      className={cn(
                        'w-full justify-start text-left h-auto min-h-[2.5rem] py-2 px-3',
                        'disabled:border-input disabled:opacity-100 disabled:bg-muted disabled:pointer-events-none',
                        !selectedOption && 'text-muted-foreground',
                        className
                      )}
                      style={
                        warning
                          ? {
                              outline: `2px solid ${DEFAULT_COLOR_PALETTE.amber}`,
                              outlineOffset: '2px',
                            }
                          : undefined
                      }
                      disabled={disabled || loading}
                    >
                      {renderTrigger ? (
                        renderTrigger(selectedOption, defaultPlaceholder)
                      ) : (
                        <DefaultTriggerRenderer
                          selectedOption={selectedOption}
                          placeholder={defaultPlaceholder}
                          loading={loading}
                          isOpen={isOpen}
                          showAvatars={showAvatars}
                          showDescriptionInSelector={showDescriptionInSelector}
                          avatarShape={avatarShape}
                          avatarSize={avatarSize}
                          hasClearButton={isClearSelectionEnabled}
                        />
                      )}
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent
                  // `p-1` (not `p-0`) so option highlights never touch the
                  // popover's rounded corners (the first/last item's highlight
                  // was getting clipped against the edge).
                  className="w-[var(--radix-popover-trigger-width)] p-1"
                  align="start"
                >
                  {shouldVirtualize ? (
                    <SingleSelectVirtualizedList
                      options={filteredOptions}
                      selectedValue={field.value || ''}
                      onSelectOption={handleSelectOption}
                      showAvatars={showAvatars}
                      showDescriptionInOptions={showDescriptionInOptions}
                      avatarShape={avatarShape}
                      avatarSize={avatarSize}
                      maxHeight={maxHeight}
                      emptyMessage={defaultEmptyMessage}
                      searchable={searchable}
                      hideSearchWhenEmpty={hideSearchWhenEmpty}
                      searchValue={searchValue}
                      searchPlaceholder={defaultSearchPlaceholder}
                      onSearchChange={handleSearchChange}
                      isOpen={isOpen}
                      renderOption={renderOption}
                    />
                  ) : (
                    <SingleSelectCommandListComponent
                      options={filteredOptions}
                      selectedValue={field.value || ''}
                      onSelectOption={handleSelectOption}
                      renderOption={renderOption}
                      defaultRenderOption={defaultRenderOption}
                      renderGroupHeading={renderGroupHeading}
                      searchable={searchable}
                      hideSearchWhenEmpty={hideSearchWhenEmpty}
                      searchValue={searchValue}
                      searchPlaceholder={defaultSearchPlaceholder}
                      onSearchChange={handleSearchChange}
                      maxHeight={maxHeight}
                      emptyMessage={defaultEmptyMessage}
                      noOptionsMessage={noOptionsMessage}
                      hasNextPage={hasNextPage}
                      isFetchingNextPage={isFetchingNextPage}
                      setContainerElement={setContainerElement}
                      setSentinelElement={setSentinelElement}
                      isCreatable={isCreatable}
                      onCreateOption={onCreateOption}
                    />
                  )}
                </PopoverContent>
              </Popover>
              {showClearButton && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="absolute right-8 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-sm hover:bg-accent"
                  aria-label={t('clearSelection')}
                >
                  <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                </button>
              )}
            </div>
            {showFormMessage && <FormMessage />}
          </FormItem>
        );
      }}
    />
  );
}
export type { SingleSelectOption, SingleSelectProps } from './single-select-types';
